import { useState, type FormEvent, type ReactNode } from 'react';

export interface LoginViewProps {
  /**
   * Attempts the login; resolves to an Estonian error message to show, or
   * null on success (the auth listener then swaps this view for the app).
   */
  onSubmit: (email: string, password: string) => Promise<string | null>;
  /**
   * Sends the Supabase password-reset email; resolves to an Estonian error
   * message, or null when the email was accepted.
   */
  onRequestReset: (email: string) => Promise<string | null>;
  /** A pre-existing auth problem (e.g. missing Supabase config) shown above the form. */
  initialError?: string | null;
}

/**
 * The production login screen (Estonian UI): Supabase email+password against
 * the same project as the API, so the resulting JWT carries RLS exactly like
 * every other environment. Kept presentation-only — the Supabase calls arrive
 * via `onSubmit` / `onRequestReset` (src/auth/useAuth), so this component is a
 * pure form. "Unustasid parooli?" flips the same card into a reset-request
 * form that emails the Supabase recovery link.
 */
export function LoginView({ onSubmit, onRequestReset, initialError }: LoginViewProps): ReactNode {
  const [mode, setMode] = useState<'login' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    if (mode === 'login') {
      const message = await onSubmit(email, password);
      setPending(false);
      if (message) setError(message);
      return;
    }
    const message = await onRequestReset(email);
    setPending(false);
    if (message) {
      setError(message);
      return;
    }
    setResetSent(true);
  };

  const switchMode = (next: 'login' | 'reset'): void => {
    setMode(next);
    setError(null);
    setResetSent(false);
  };

  const shownError = error ?? initialError ?? null;
  const isReset = mode === 'reset';

  return (
    <div className="flex h-screen items-center justify-center bg-background text-foreground">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6"
        aria-label={isReset ? 'Parooli taastamine' : 'Sisselogimine'}
      >
        <div>
          <h1 className="text-base font-semibold tracking-tight">Progear Finance</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {isReset
              ? 'Sisesta oma e-post — saadame parooli taastamise lingi'
              : 'Logi sisse oma kontoga'}
          </p>
        </div>

        {shownError ? (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {shownError}
          </p>
        ) : null}

        {isReset && resetSent ? (
          <p role="status" className="rounded-md border border-border bg-accent px-3 py-2 text-xs">
            Saatsime parooli taastamise lingi aadressile {email}. Kontrolli postkasti (ka
            rämpsposti) ja ava kirjas olev link.
          </p>
        ) : null}

        <label className="block text-xs">
          <span className="mb-1 block text-muted-foreground">E-post</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-ring"
          />
        </label>

        {isReset ? null : (
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">Parool</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-ring"
            />
          </label>
        )}

        <button
          type="submit"
          disabled={pending}
          className="h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-60"
        >
          {isReset
            ? pending
              ? 'Saadan…'
              : 'Saada taastamise link'
            : pending
              ? 'Login sisse…'
              : 'Logi sisse'}
        </button>

        <div className="text-center">
          {isReset ? (
            <button
              type="button"
              onClick={() => switchMode('login')}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Tagasi sisselogimise juurde
            </button>
          ) : (
            <button
              type="button"
              onClick={() => switchMode('reset')}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Unustasid parooli?
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
